import { useEffect, useState, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Activity, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const fmtMs = (ms) => {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms.toFixed(0)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
};

const barPct = (ms, max) => {
  if (!max) return 0;
  return Math.min(100, (ms / max) * 100);
};

const perfClass = (ms) => {
  if (ms < 200) return 'text-emerald-700';
  if (ms < 1000) return 'text-amber-700';
  return 'text-red-700 font-semibold';
};

export default function PerfDashboardPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('p95_ms');
  const [sortDir, setSortDir] = useState('desc');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/debug/perf?min_samples=1');
      setRows(data.endpoints || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load perf snapshot');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!autoRefresh) return;
    const iv = setInterval(load, 5000);
    return () => clearInterval(iv);
  }, [autoRefresh]);

  const sorted = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sortBy] ?? 0;
      const bv = b[sortBy] ?? 0;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [rows, sortBy, sortDir]);

  const maxP95 = useMemo(() => Math.max(...rows.map(r => r.p95_ms || 0), 1), [rows]);

  const setSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };

  const H = ({ col, label, align = 'right' }) => (
    <th onClick={() => setSort(col)} className={`pb-2 text-[10px] uppercase tracking-wider text-muted-foreground cursor-pointer select-none hover:text-ink ${align === 'right' ? 'text-right' : 'text-left'}`} data-testid={`perf-sort-${col}`}>
      {label} {sortBy === col && (sortDir === 'asc' ? '↑' : '↓')}
    </th>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-12" data-testid="perf-loading">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="perf-dashboard">
      <div className="flex items-center gap-2 flex-wrap">
        <Activity className="h-4 w-4 text-ink" />
        <h1 className="text-base font-semibold text-ink">Endpoint Performance</h1>
        <span className="text-xs text-muted-foreground">
          ({rows.length} endpoints · in-memory ring, last ~500 samples per endpoint)
        </span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
            onClick={load} data-testid="perf-refresh">
            <RefreshCw className="h-3 w-3" /> Refresh
          </Button>
          <Button size="sm" variant={autoRefresh ? 'default' : 'outline'} className="h-7 text-xs"
            onClick={() => setAutoRefresh(!autoRefresh)} data-testid="perf-auto">
            Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </div>
      </div>

      <Card className="bg-white border-stone/50">
        <CardContent className="p-4">
          {rows.length === 0 ? (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No samples yet. Ring buffer is cleared on backend restart.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-stone/30">
                  <H col="endpoint" label="Endpoint" align="left" />
                  <H col="method" label="Method" align="left" />
                  <H col="count" label="Samples" />
                  <H col="p50_ms" label="p50" />
                  <H col="p95_ms" label="p95" />
                  <H col="p99_ms" label="p99" />
                  <H col="max_ms" label="Max" />
                  <H col="avg_ms" label="Avg" />
                  <H col="errors" label="5xx" />
                </tr>
              </thead>
              <tbody>
                {sorted.map(r => (
                  <tr key={`${r.method} ${r.endpoint}`} className="border-b border-stone/10 hover:bg-stone/5" data-testid={`perf-row-${r.endpoint}`}>
                    <td className="py-1.5 font-mono text-[10px] text-ink relative">
                      <div className="absolute inset-y-0 left-0 bg-stone/10" style={{ width: `${barPct(r.p95_ms, maxP95)}%` }} />
                      <span className="relative">{r.endpoint}</span>
                    </td>
                    <td className="py-1.5 text-[10px] uppercase text-muted-foreground">{r.method}</td>
                    <td className="py-1.5 text-right">{r.count}</td>
                    <td className={`py-1.5 text-right ${perfClass(r.p50_ms)}`}>{fmtMs(r.p50_ms)}</td>
                    <td className={`py-1.5 text-right ${perfClass(r.p95_ms)}`}>{fmtMs(r.p95_ms)}</td>
                    <td className={`py-1.5 text-right ${perfClass(r.p99_ms)}`}>{fmtMs(r.p99_ms)}</td>
                    <td className={`py-1.5 text-right ${perfClass(r.max_ms)}`}>{fmtMs(r.max_ms)}</td>
                    <td className={`py-1.5 text-right ${perfClass(r.avg_ms)}`}>{fmtMs(r.avg_ms)}</td>
                    <td className="py-1.5 text-right">
                      {r.errors > 0 ? <span className="text-red-700 font-semibold">{r.errors}</span> : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
